import { motion, useReducedMotion } from 'motion/react';
import { Brain, Clock, PaperPlaneTilt, SquaresFour, CheckCircle, LockKey } from '@phosphor-icons/react';
import { SpotlightCard } from './motion/SpotlightCard';

const features = [
  {
    icon: Brain,
    title: 'Smart email parsing',
    body: 'Understands context, requirements, and what a follow-up actually needs to contain.',
    featured: true,
    tinted: true,
    gridClass: 'md:col-span-2 md:row-span-2 md:col-start-1 md:row-start-1',
  },
  {
    icon: Clock,
    title: 'Automatic timeframes',
    body: 'Extracts follow-up deadlines from your emails and schedules reminders around them.',
    gridClass: 'md:col-start-3 md:row-start-1',
  },
  {
    icon: PaperPlaneTilt,
    title: 'Automated follow-ups',
    body: 'Sends and re-sends follow-up emails until you get a real response.',
    tinted: true,
    gridClass: 'md:col-start-3 md:row-start-2',
  },
  {
    icon: SquaresFour,
    title: 'Dashboard visibility',
    body: 'See every active follow-up, its status, and what the AI is pursuing on your behalf.',
    gridClass: 'md:col-start-1 md:row-start-3',
  },
  {
    icon: CheckCircle,
    title: 'Response verification',
    body: 'Checks that replies actually meet what you originally asked for.',
    tinted: true,
    gridClass: 'md:col-start-2 md:row-start-3',
  },
  {
    icon: LockKey,
    title: 'Secure and private',
    body: 'Your emails and data are encrypted, and never used for anything but your follow-ups.',
    gridClass: 'md:col-start-3 md:row-start-3',
  },
];

export function Features() {
  const reduce = useReducedMotion();

  return (
    <section id="features" className="py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl md:text-4xl font-bold text-zinc-100 text-center mb-16">
          Everything to be your virtual assistant for emails
        </h2>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.15 }}
          variants={{ hidden: {}, visible: { transition: { staggerChildren: reduce ? 0 : 0.08 } } }}
          className="grid gap-5 md:grid-cols-3 md:grid-rows-3"
        >
          {features.map((feature) => (
            <motion.div
              key={feature.title}
              variants={{
                hidden: reduce ? {} : { opacity: 0, y: 20 },
                visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] } },
              }}
              className={feature.gridClass}
            >
              <SpotlightCard
                className={`h-full rounded-xl border p-6 transition-colors duration-300 ${
                  feature.tinted
                    ? 'border-indigo-400/20 bg-indigo-500/[0.08] hover:border-indigo-400/40'
                    : 'border-white/10 bg-white/[0.04] hover:border-white/20'
                }`}
              >
                <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-indigo-500/15">
                  <feature.icon weight="bold" className="h-5 w-5 text-indigo-300" />
                </div>
                <h3 className={`mt-4 font-semibold text-zinc-100 ${feature.featured ? 'text-2xl' : 'text-lg'}`}>
                  {feature.title}
                </h3>
                <p className={`mt-2 text-zinc-400 ${feature.featured ? 'max-w-sm text-base' : 'text-sm'}`}>
                  {feature.body}
                </p>
              </SpotlightCard>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
